import {
  useEmployeeProfessionalCreate,
  useEmployeeProfessionalEdit,
  useEmployeeUpdate,
} from '@/core/domain/enterprise/Employees/Employees.hooks';
import {
  EmployeeEditFormValues,
  EmployeeProfessionalFormValues,
} from '@/core/domain/enterprise/Employees/Employees.types';
import { useDefaultFormErrorNotification, useServiceError } from '@/hooks';
import { store } from '@/Root';
import { authOperations } from '@/state/ducks/auth';
import { Form, FormInstance } from 'antd';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import handlerPersonalUpdate from './handlers/personal';
import handlerPersonalValues from './handlers/professional';

type Props = PropsWithChildren<unknown>;
type Forms = {
  employeePersonal?: EmployeeEditFormValues;
  employeeProfessional?: EmployeeProfessionalFormValues;
};
type InstanceForms = {
  employeePersonal?: FormInstance<EmployeeEditFormValues>;
  employeeProfessional?: FormInstance<EmployeeProfessionalFormValues>;
};

type EmployeeFormContextProps = {
  isLoading: boolean;
  forms: Forms;
  employeeId?: number;
  disabled: boolean;
  instanceForms: InstanceForms;
};

const EmployeeFormContext = createContext({} as EmployeeFormContextProps);
export const useEmployeeFormContext = () => useContext(EmployeeFormContext);

const EmployeeFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { params } = useRouteMatch<{ employeeId?: string }>();

  // State
  const [forms, setForms] = useState<Forms>({});
  const [instanceForms, setInstanceForms] = useState<InstanceForms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState<number>();

  // Request hook
  const personalUpdate = useEmployeeUpdate();
  const professionalUpdate = useEmployeeProfessionalEdit();
  const professionalCreate = useEmployeeProfessionalCreate();

  const disabled = !Object.keys(forms).some((key) => !!key);

  useServiceError(
    personalUpdate.error ||
      professionalUpdate.error ||
      professionalCreate.error,
  );

  useEffect(() => {
    setForms({});
    if (params.employeeId) {
      setEmployeeId(Number(params.employeeId));
    }
  }, [params.employeeId]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setInstanceForms((state) => ({ ...state, [name]: info.forms[name] }));
      setForms((state) => ({
        ...state,
        [name]: info.forms[name]?.getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    setIsLoading(true);

    try {
      if (forms.employeePersonal) {
        await handlerPersonalUpdate(forms.employeePersonal, (values) =>
          personalUpdate.mutateAsync(values),
        );

        await store.dispatch(authOperations.handleRefreshToken());
      }

      if (forms.employeeProfessional) {
        await handlerPersonalValues(
          forms.employeeProfessional,
          async (values) => {
            if (values.id) {
              await professionalUpdate.mutateAsync({
                ...values,
                employeeId: Number(employeeId),
              });
            } else {
              await professionalCreate.mutateAsync({
                ...values,
                employeeId: Number(employeeId),
              });
            }
          },
        );
      }

      setForms({});
    } catch (error: unknown) {
      const err = error as AxiosError;

      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }

    setIsLoading(false);
  }

  return (
    <EmployeeFormContext.Provider
      value={{ isLoading, forms, employeeId, disabled, instanceForms }}
    >
      <Form.Provider
        onFormFinish={handleSubmit}
        onFormChange={handleFormChange}
      >
        {children}
      </Form.Provider>
    </EmployeeFormContext.Provider>
  );
};

export default EmployeeFormProvider;
